// packages/api/src/SiteBrandService.ts
//
// A2e #101 — the brand a page of the public site speaks as (`siteBrand` on eventsmagic-api), for the DOMAIN it was
// served on: a host's own custom domain is a full white label (its name everywhere); any other domain speaks as the
// brand of its space, and with `eventSlug` the page NAME is the guest-facing one for that event (the organizer / "Hosted
// by" when its owner's plan white-labels). Never a name written in code — the white label decides.
import { gql } from "@apollo/client";
import { OpResult } from "@sdflc/api-helpers";

import { ApiService } from "./ApiService";

export interface SiteBrand {
  /** The name the page shows (a tab title). */
  name?: string | null;
  /** The link previews' site name (og:site_name) — a custom domain's own name, else the brand's, never an organizer's. */
  siteName?: string | null;
  /** Where "Powered by" links. */
  websiteUrl?: string | null;
  logoUrl?: string | null;
  /** 2026-10-22 — the browser tab icon: the brand's square mark (a host's own domain: its own); null when none is set. */
  iconUrl?: string | null;
  /** A host's own custom domain (a full white label). */
  isCustomDomain?: boolean | null;
  /** The space the website belongs to — e.g. the stock-photo services' referral source. */
  spaceId?: string | null;
  /** A12.3 — the PLATFORM's privacy policy (never a white label's); null when unknown. */
  privacyUrl?: string | null;
}

// ⓘ `spaceId` and `privacyUrl` (A12.3) are newer than the first `siteBrand`, and `iconUrl` (2026-10-22) newer still: a
// gateway that does not know one refuses the WHOLE query, so the read asks again without the newest it refused (first
// without the icon — the generic tab icon; then without the space — no referral tag on credits, no privacy link) rather
// than losing the brand — the site is deployed after the API, but a recompose can lag.
const siteBrandQuery = (withSpace: boolean, withIcon = false) => gql`
  query SiteBrand($domain: String, $eventSlug: String) {
    siteBrand(domain: $domain, eventSlug: $eventSlug) {
      code
      errors {
        name
        errors
      }
      data {
        name
        siteName
        websiteUrl
        logoUrl
        isCustomDomain
        ${withSpace ? "spaceId privacyUrl" : ""}
        ${withIcon ? "iconUrl" : ""}
      }
    }
  }
`;
const SITE_BRAND_QUERY_WITH_ICON = siteBrandQuery(true, true);
const SITE_BRAND_QUERY = siteBrandQuery(true);
const SITE_BRAND_QUERY_BASE = siteBrandQuery(false);

/** Did a gateway refuse `iconUrl` by name (one older than the tab icon)? */
export const refusedSiteBrandIcon = (result: any): boolean =>
  !!result?.didFail?.() &&
  (Array.isArray(result?.errors) ? result.errors : []).some((e: any) =>
    (Array.isArray(e?.errors) ? e.errors : []).some((m: unknown) => /Cannot query field ["']iconUrl["'] on type ["']SiteBrand["']/.test(String(m))),
  );

/** Did a gateway refuse `spaceId` or `privacyUrl` by name (an older gateway), rather than fail for any other reason? */
export const refusedSiteBrandSpace = (result: any): boolean =>
  !!result?.didFail?.() &&
  (Array.isArray(result?.errors) ? result.errors : []).some((e: any) =>
    (Array.isArray(e?.errors) ? e.errors : []).some((m: unknown) => /Cannot query field ["'](spaceId|privacyUrl)["'] on type ["']SiteBrand["']/.test(String(m))),
  );

export class SiteBrandService {
  /** The brand for `domain` (and, optionally, the event at `eventSlug` on it). Always a fresh network read. */
  static async get(args: { domain: string; eventSlug?: string | null }, options: Record<string, any> = {}): Promise<OpResult> {
    const variables = { domain: args.domain, eventSlug: args.eventSlug ?? null };
    const opts = { fetchPolicy: "network-only", ...options };
    const withIcon = await ApiService.executeQuery(SITE_BRAND_QUERY_WITH_ICON, variables, opts);

    if (!refusedSiteBrandIcon(withIcon)) {
      return withIcon;
    }

    const result = await ApiService.executeQuery(SITE_BRAND_QUERY, variables, opts);

    return refusedSiteBrandSpace(result) ? ApiService.executeQuery(SITE_BRAND_QUERY_BASE, variables, opts) : result;
  }
}
